import clsx from "clsx";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Trash2 } from "~/common/redesign-v1/icons";

function PlayOutline(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M2.82031 1.17084C2.94068 1.10513 3.08726 1.11039 3.20264 1.18451L10.2026 5.68451C10.31 5.75351 10.375 5.87234 10.375 5.99994C10.375 6.12754 10.31 6.24637 10.2026 6.31537L3.20264 10.8154C3.08726 10.8895 2.94068 10.8948 2.82031 10.829C2.69992 10.7633 2.625 10.6371 2.625 10.4999V1.49994C2.625 1.36277 2.69992 1.23658 2.82031 1.17084ZM3.375 9.81293L9.30664 5.99994L3.375 2.18647V9.81293Z"
        fill="currentColor"
      />
    </svg>
  );
}

function PauseOutline(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M8.625 2.5C8.625 2.43096 8.56904 2.375 8.5 2.375H7.5C7.43096 2.375 7.375 2.43096 7.375 2.5V9.5C7.375 9.56903 7.43096 9.625 7.5 9.625H8.5C8.56904 9.625 8.625 9.56903 8.625 9.5V2.5ZM9.375 9.5C9.375 9.98325 8.98325 10.375 8.5 10.375H7.5C7.01675 10.375 6.625 9.98325 6.625 9.5V2.5C6.625 2.01675 7.01675 1.625 7.5 1.625H8.5C8.98325 1.625 9.375 2.01675 9.375 2.5V9.5Z"
        fill="currentColor"
      />
      <path
        d="M4.625 2.5C4.625 2.43096 4.56904 2.375 4.5 2.375H3.5C3.43096 2.375 3.375 2.43096 3.375 2.5V9.5C3.375 9.56903 3.43096 9.625 3.5 9.625H4.5C4.56904 9.625 4.625 9.56903 4.625 9.5V2.5ZM5.375 9.5C5.375 9.98325 4.98325 10.375 4.5 10.375H3.5C3.01675 10.375 2.625 9.98325 2.625 9.5V2.5C2.625 2.01675 3.01675 1.625 3.5 1.625H4.5C4.98325 1.625 5.375 2.01675 5.375 2.5V9.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

type AudioEntryRowProps = {
  number: number;
  sourceUrl: string | null;
  durationText: string;
  timestampText: string;
  isPlaying: boolean;
  playbackRate: number;
  onTogglePlayback: () => void;
  onPlaybackRateChange: (rate: number) => void;
  setAudioRef: (node: HTMLAudioElement | null) => void;
  onEnded: () => void;
  onRemove?: () => void;
  removeDisabled?: boolean;
  removeDisabledTooltip?: string;
};

export function AudioEntryRow({
  number,
  sourceUrl,
  durationText,
  timestampText,
  isPlaying,
  playbackRate,
  onTogglePlayback,
  onPlaybackRateChange,
  setAudioRef,
  onEnded,
  onRemove,
  removeDisabled = false,
  removeDisabledTooltip,
}: AudioEntryRowProps) {
  const intl = useIntl();

  return (
    <div
      className={clsx(
        "tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 tw-rounded-[10px] tw-border tw-border-solid tw-border-border tw-p-4",
        isPlaying ? "tw-bg-[#F5F5F5]" : "tw-bg-background",
      )}
    >
      <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
        <button
          type="button"
          className={clsx(
            "tw-flex tw-size-[34px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-text-white disabled:tw-cursor-not-allowed disabled:tw-opacity-50",
            isPlaying ? "tw-bg-[#57534e] tw-ring-2 tw-ring-border-3" : "tw-bg-[#78716c]",
          )}
          disabled={!sourceUrl}
          onClick={onTogglePlayback}
          aria-label={isPlaying ? "pause-audio" : "play-audio"}
        >
          {isPlaying ? <PauseOutline className="tw-size-3" /> : <PlayOutline className="tw-size-3" />}
        </button>
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
          <div className="tw-truncate tw-text-sm tw-font-semibold tw-text-foreground">
            <FormattedMessage id="additional.services.smart.audio.record_name" values={{ number }} />
          </div>
          <div className="tw-truncate tw-text-xs tw-text-muted-foreground">{timestampText}</div>
        </div>
      </div>

      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
        <div
          className="tw-flex tw-overflow-hidden tw-rounded tw-border tw-border-solid tw-border-border"
          role="group"
          aria-label={intl.formatMessage({ id: "additional.services.smart.audio.speed_toggle" })}
        >
          {[1, 1.5, 2].map((rate) => (
            <button
              key={rate}
              type="button"
              className={clsx(
                "tw-border-0 tw-border-r tw-border-solid tw-border-border tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-tabular-nums last:tw-border-r-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-40",
                playbackRate === rate ? "tw-bg-foreground tw-text-background" : "tw-bg-background tw-text-foreground",
              )}
              disabled={!sourceUrl}
              onClick={() => onPlaybackRateChange(rate)}
              aria-pressed={playbackRate === rate}
            >
              X{rate}
            </button>
          ))}
        </div>
        <span className="tw-rounded tw-bg-secondary-hover tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-tabular-nums tw-text-secondary-foreground">{durationText}</span>
        {onRemove ? (
          <button
            type="button"
            className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-transparent tw-text-foreground disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
            disabled={removeDisabled}
            onClick={onRemove}
            aria-label="remove-audio"
            title={removeDisabled ? removeDisabledTooltip : undefined}
          >
            <Trash2 className="tw-size-4" />
          </button>
        ) : null}
      </div>

      {sourceUrl ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- doctor audio playback only
        <audio ref={setAudioRef} src={sourceUrl} className="tw-hidden" onEnded={onEnded} />
      ) : null}
    </div>
  );
}
